import React, { useMemo } from 'react';
import { Autocomplete, Checkbox, Chip, Stack, TextField } from '@mui/material';
import {
  LANGUAGE_OPTIONS,
  LanguageOption,
  sortLanguageOptionsByLabel,
} from 'src/utils/languages';
import { CountryLabel, LanguagesLayout } from './Configuration.styles';

interface LanguagesSectionProps {
  selectedLanguages: LanguageOption[];
  setSelectedLanguages: (languages: LanguageOption[]) => void;
  languageTagsReadOnly?: boolean;
}

export const LanguagesSection: React.FC<LanguagesSectionProps> = ({
  selectedLanguages,
  setSelectedLanguages,
  languageTagsReadOnly = false,
}) => {
  const sortedSelectedLanguages = useMemo(
    () => sortLanguageOptionsByLabel(selectedLanguages),
    [selectedLanguages],
  );

  const defaultLanguageValue = selectedLanguages.find(
    (lang) => lang.isDefault,
  )?.value;

  return (
    <LanguagesLayout data-testid="languages-section">
      <CountryLabel>Languages</CountryLabel>
      <Autocomplete
        multiple
        disableCloseOnSelect
        fullWidth
        options={LANGUAGE_OPTIONS}
        value={sortedSelectedLanguages}
        disabled={languageTagsReadOnly}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionDisabled={(option) => {
          if (languageTagsReadOnly) return true;
          const selectedLang = selectedLanguages.find(
            (selected) => selected.value === option.value,
          );
          return Boolean(selectedLang?.isDefault);
        }}
        onChange={(_event, newValue) => {
          const nextSelected = newValue.filter(Boolean) as LanguageOption[];

          const updatedLanguages = nextSelected.map((lang) => {
            const existingLang = selectedLanguages.find(
              (existing) => existing.value === lang.value,
            );
            if (existingLang?.isDefault) {
              return { ...lang, isDefault: true };
            }
            return lang;
          });

          const hasDefault = updatedLanguages.some(
            (lang) => lang.value === defaultLanguageValue,
          );

          if (!hasDefault && defaultLanguageValue) {
            const defaultLang = selectedLanguages.find(
              (lang) => lang.value === defaultLanguageValue,
            );
            if (defaultLang) {
              setSelectedLanguages([...updatedLanguages, defaultLang]);
              return;
            }
          }

          setSelectedLanguages(updatedLanguages);
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li
              key={key}
              {...optionProps}
              data-testid={`${option.label}-select`}
            >
              <Checkbox
                checked={selected}
                style={{ marginRight: 8 }}
                data-testid={`multi-select-option-${option.label}`}
              />
              {option.label}
            </li>
          );
        }}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Add languages"
            inputProps={{
              ...params.inputProps,
              'data-testid': 'add-language-dropdown-select',
              'aria-label': 'Add languages',
            }}
          />
        )}
        data-testid="add-language-dropdown-select"
      />
      {sortedSelectedLanguages.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} mt={1}>
          {sortedSelectedLanguages.map((option) => {
            const isDefault = option.isDefault === true;
            return (
              <Chip
                key={option.value}
                label={option.label}
                size="small"
                variant="outlined"
                onDelete={
                  isDefault || languageTagsReadOnly
                    ? undefined
                    : () =>
                        setSelectedLanguages(
                          selectedLanguages.filter(
                            (l) => l.value !== option.value,
                          ),
                        )
                }
              />
            );
          })}
        </Stack>
      )}
    </LanguagesLayout>
  );
};
